import { useEvent } from 'react-use-event-hook'
import { analytics } from 'services/Analytics/Analytics'
import {
  getBagAnalytics,
  getCartItemContext,
} from 'services/Analytics/bagAnalytics'
import { useExperimentManager } from 'services/ExperimentManager/ExperimentManager'
import { readMobxSnapshot } from 'utils/mobx/readOutsideReaction'
import { useCartStoreOrNull } from 'stores/cartStore/CartStoreProvider'
import type { CartItem } from 'stores/cartStore/CartItem'
import { useCheckoutPromoInputs } from './useCheckoutPromoInputs'
import { useDiscountInfo } from './useDiscountInfo'
import { useGroupCheckoutPricing } from './useGroupCheckoutPricing'

export function useBagAnalytics() {
  const cartStore = useCartStoreOrNull()
  const groupPricing = useGroupCheckoutPricing()
  const discountInfo = useDiscountInfo()
  const { promocode } = useCheckoutPromoInputs()
  const experimentManager = useExperimentManager()

  const getBagProps = useEvent(() =>
    readMobxSnapshot(() => ({
      ...getBagAnalytics({
        tilesItem: cartStore?.tilesItem,
        photoBookItems: cartStore?.photoBookItems ?? [],
        pricingData: groupPricing.data,
        discountInfo,
        promocode,
        isPriceTierEnabled: experimentManager.isEnabled(
          'pricing_photo_book_price_tier'
        ),
      }),
      Source: 'bag',
    }))
  )

  const getBagItemProps = useEvent((item: CartItem) =>
    readMobxSnapshot(() => ({
      ...getBagProps(),
      ...getCartItemContext(item),
    }))
  )

  const trackBagEvent = useEvent(
    (eventName: string, props: Record<string, unknown> = {}) => {
      analytics.track(eventName, { ...getBagProps(), ...props })
    }
  )

  const trackBagItemEvent = useEvent(
    (
      eventName: string,
      item: CartItem,
      props: Record<string, unknown> = {}
    ) => {
      analytics.track(eventName, { ...getBagItemProps(item), ...props })
    }
  )

  const trackBagItemEventOnce = useEvent(
    (
      eventName: string,
      dedupId: string,
      item: CartItem,
      props: Record<string, unknown> = {}
    ) => {
      analytics.trackOncePerId(eventName, dedupId, {
        ...getBagItemProps(item),
        ...props,
      })
    }
  )

  return {
    getBagProps,
    trackBagEvent,
    trackBagItemEvent,
    trackBagItemEventOnce,
  }
}
